import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Video, Inbox, Plus, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader, adminBtnPrimary, StatusBadge } from "@/components/admin/ui";
import { formatDate } from "@/lib/api";

export default function Dashboard() {
  const [articles, setArticles] = useState([]);
  const [videos, setVideos] = useState([]);
  const [inquiries, setInquiries] = useState([]);

  useEffect(() => {
    api.get("/admin/articles").then((r) => setArticles(r.data)).catch(() => {});
    api.get("/admin/videos").then((r) => setVideos(r.data)).catch(() => {});
    api.get("/admin/inquiries").then((r) => setInquiries(r.data)).catch(() => {});
  }, []);

  const count = (arr, status) => arr.filter((x) => x.status === status).length;
  const newInquiries = inquiries.filter((i) => i.status === "new").length;

  const stats = [
    { label: "Published Articles", value: count(articles, "published"), sub: `${count(articles, "draft")} drafts · ${count(articles, "scheduled")} scheduled`, icon: FileText, to: "/admin/articles" },
    { label: "Videos", value: videos.length, sub: `${count(videos, "published")} published`, icon: Video, to: "/admin/videos" },
    { label: "New Inquiries", value: newInquiries, sub: `${inquiries.length} total`, icon: Inbox, to: "/admin/inquiries" },
  ];

  return (
    <>
      <PageHeader title="Dashboard" subtitle="An overview of your content and inquiries.">
        <Link to="/admin/articles/new" className={adminBtnPrimary}><Plus className="h-4 w-4" /> New Article</Link>
      </PageHeader>

      <div className="grid gap-5 sm:grid-cols-3">
        {stats.map((s) => (
          <Link key={s.label} to={s.to} className="group border border-ink/10 bg-cream-100 p-6 transition-colors hover:border-gold-500" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center justify-between">
              <s.icon className="h-6 w-6 text-gold-500" strokeWidth={1.5} />
              <ArrowRight className="h-4 w-4 text-steel-300 transition-colors group-hover:text-gold-600" />
            </div>
            <p className="mt-5 font-serif text-5xl font-light text-ink">{s.value}</p>
            <p className="mt-1 font-sans text-sm font-medium text-ink">{s.label}</p>
            <p className="font-sans text-xs text-steel-500">{s.sub}</p>
          </Link>
        ))}
      </div>

      <div className="mt-10 grid gap-8 lg:grid-cols-2">
        <div className="border border-ink/10 bg-cream-100 p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-serif text-2xl font-light text-ink">Recent Articles</h2>
            <Link to="/admin/articles" className="font-sans text-sm text-gold-600 gf-link">Manage</Link>
          </div>
          {articles.slice(0, 5).map((a) => (
            <Link key={a.id} to={`/admin/articles/${a.id}`} className="flex items-center justify-between border-t border-cream-200 py-3">
              <span className="font-sans text-sm text-ink line-clamp-1">{a.title || "Untitled"}</span>
              <StatusBadge status={a.status} />
            </Link>
          ))}
          {articles.length === 0 && <p className="py-6 text-center font-sans text-sm text-steel-400">No articles yet.</p>}
        </div>

        <div className="border border-ink/10 bg-cream-100 p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-serif text-2xl font-light text-ink">Recent Inquiries</h2>
            <Link to="/admin/inquiries" className="font-sans text-sm text-gold-600 gf-link">View all</Link>
          </div>
          {inquiries.slice(0, 5).map((i) => (
            <div key={i.id} className="flex items-center justify-between border-t border-cream-200 py-3">
              <span className="font-sans text-sm text-ink">{i.name} · <span className="text-steel-500">{i.area}</span></span>
              <span className="font-sans text-xs text-steel-400">{formatDate(i.created_at)}</span>
            </div>
          ))}
          {inquiries.length === 0 && <p className="py-6 text-center font-sans text-sm text-steel-400">No inquiries yet.</p>}
        </div>
      </div>
    </>
  );
}
